import React, { useState, useEffect, useCallback } from 'react';
import { accessPolicyService } from '../services/api';

// V277 doors doctrine: only doors that exist as access_policies rows render —
// the component maps whatever the server returns, so a future door appears
// without a client change (with a raw-name fallback label). media_console and
// the driver surface are invite/provision-only BY CONSTRUCTION and never get
// a row.
const DOOR_META = {
  brands: {
    label: 'Brands Manager self-serve signup',
    description: 'What happens when a stranger signs up for the Brands Manager',
  },
};

const MODE_META = {
  queue: { label: 'Approval queue', color: '#e65100' },
  open: { label: 'Open — instant access', color: '#228b22' },
};

const Settings = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  // The policy row awaiting flip confirmation (drives the modal).
  const [flipDoor, setFlipDoor] = useState(null);

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accessPolicyService.getAll();
      setPolicies(response.data || []);
    } catch (err) {
      console.error('Error fetching access policies:', err);
      setError(err.response?.data?.error || 'Failed to load access policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'medium',
    });
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div className="detail-section">
          <div className="section-header">
            <h2>Access Policies</h2>
          </div>
          <p style={{ color: '#666', fontSize: '0.85rem' }}>
            What each signup door does with a stranger. Invited users are never queued, whatever the door says.
          </p>
          <div className="tenant-list">
            {policies.map((row) => {
              const doorMeta = DOOR_META[row.door] || { label: row.door };
              const modeMeta = MODE_META[row.mode] || { label: row.mode, color: '#666' };
              return (
                <div key={row.door} className="tenant-assignment-item">
                  <div className="tenant-info">
                    <span className="tenant-name">{doorMeta.label}</span>
                    {doorMeta.description && (
                      <span style={{ color: '#666', fontSize: '0.8rem' }}>{doorMeta.description}</span>
                    )}
                  </div>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    background: `${modeMeta.color}15`,
                    color: modeMeta.color,
                    border: `1px solid ${modeMeta.color}30`,
                    whiteSpace: 'nowrap',
                  }}>
                    {modeMeta.label}
                  </span>
                  <div className="tenant-role">
                    <span className="tenant-plan">
                      Updated {formatDate(row.updated_at)}
                      {row.updated_by_name ? ` by ${row.updated_by_name}` : ' (migration seed)'}
                    </span>
                  </div>
                  {/* Opening is the dangerous act, so it wears the danger color
                      (like Disable in TenantDetail). */}
                  {row.mode === 'queue' ? (
                    <button className="btn btn-danger btn-sm" onClick={() => setFlipDoor(row)}>
                      Open door…
                    </button>
                  ) : (
                    <button className="btn btn-secondary btn-sm" onClick={() => setFlipDoor(row)}>
                      Require approval…
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Refetch-after-write is the whole cache story here: the server reads
          the DB per request, so only this card could go stale. */}
      {flipDoor && (
        <FlipDoorConfirmModal
          policy={flipDoor}
          onClose={() => setFlipDoor(null)}
          onFlipped={(msg) => { setMessage(msg); setFlipDoor(null); fetchPolicies(); }}
        />
      )}
    </div>
  );
};

// Confirm-on-flip for door policy (patterned on DisableProductConfirmModal:
// modal-overlay → modal modal-sm → warning-list). Owns the update call and its
// own error state so a failure renders INSIDE the modal without closing it.
const FlipDoorConfirmModal = ({ policy, onClose, onFlipped }) => {
  const [loading, setLoading] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const target = policy.mode === 'queue' ? 'open' : 'queue';
  const label = (DOOR_META[policy.door] || { label: policy.door }).label;

  const handleConfirm = async () => {
    setLoading(true);
    setModalError(null);
    try {
      await accessPolicyService.update(policy.door, { mode: target, note });
      onFlipped(target === 'open'
        ? `${label} is now open — signups are approved instantly`
        : `${label} now requires approval`);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update door policy');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{target === 'open' ? `Open the ${label} door?` : `Return ${label} to the approval queue?`}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {modalError && <div className="alert alert-error">{modalError}</div>}
          {target === 'open' ? (
            <>
              <p>Approve <strong>{label}</strong> automatically?</p>
              <ul className="warning-list">
                <li>New brand signups will be approved instantly, no human in the loop</li>
                <li>Invited users are unaffected — invitations were never queued</li>
                <li>Reversible — flip back to Approval queue at any time</li>
              </ul>
            </>
          ) : (
            <ul className="warning-list">
              <li>New brand signups will wait for a human approval before first login</li>
              <li>Users already approved keep their access</li>
            </ul>
          )}
          <div className="form-group">
            <label>Note (optional — kept with the audit trail)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={target === 'open' ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={handleConfirm}
            disabled={loading}
          >
            {target === 'open'
              ? (loading ? 'Opening…' : 'Open the door')
              : (loading ? 'Saving…' : 'Require approval')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
